<template>
    <!-- Portfolio section start -->
	<section id="portfolio-section" class="px-4 lg:px-32 py-16 relative">
        <div class="max-w-7xl mx-auto">
            <!-- Text wrapper start -->
            <div class="gap-8 flex-col md:flex-row">
                <!-- Main text start -->
                <template v-if="isLoading">
                    <LoadingSkeleton size="xl" class="mb-8"/>
                </template>
                <template v-else>
                    <h1 class="uppercase text-3xl md:text-5xl text-secondary mb-8 font-semibold">
                        {{ gallery.title }}
                    </h1>
                </template>
            </div>
            <!-- Text wrapper end -->

            <!-- Loading Grid -->
            <div v-if="isLoading" class="grid grid-cols-2 md:grid-cols-5 gap-4">
                <LoadingSkeleton v-for="i in 10" :key="i" class="aspect-square rounded-xl"/>
            </div>

            <!-- Content when loaded -->
            <template v-else>
                <!-- Mobile Slideshow -->
                <div v-if="$window.width < 768" class="relative">
                    <div v-if="processedImages.length" class="relative">
                        <div class="relative aspect-square cursor-pointer"
                             @touchstart="touchStart"
                             @touchmove="touchMove"
                             @touchend="touchEnd"
                             @click.prevent="openLightbox(currentSlide)">
                            <div class="absolute top-4 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-secondary shadow-lg pointer-events-none" aria-hidden="true">
                                <i class="fa-solid fa-up-right-and-down-left-from-center text-sm text-white"></i>
                            </div>
                            <img :src="getGalleryImageUrl(processedImages[currentSlide].url, 'mobile')" 
                                 :alt="processedImages[currentSlide].title"
                                 decoding="async"
                                 class="w-full h-full object-cover rounded-3xl">
                        </div>
                        
                        <!-- Navigation and Counter -->
                        <div class="flex items-center justify-center gap-6 mt-6">
                            <button type="button"
                                    @click.prevent="handlePrevClick" 
                                    class="bg-purple hover:bg-purple-600 text-white rounded-3xl px-6 py-2 transition-colors">
                                <i class="fa-solid fa-arrow-left text-stone-100"></i>
                            </button>
                            
                            <div class="text-center text-secondary font-medium min-w-[80px]">
                                {{ currentSlide + 1 }} / {{ processedImages.length }}
                            </div>

                            <button type="button"
                                    @click.prevent="handleNextClick" 
                                    class="bg-purple hover:bg-purple-600 text-white rounded-3xl px-6 py-2 transition-colors">
                                <i class="fa-solid fa-arrow-right text-stone-100"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Desktop Grid -->
                <div v-else 
                     class="hidden md:grid grid-cols-4 gap-6 md:gap-8">
                    <div v-for="(img, index) in processedImages" 
                         :key="index" 
                         @click="openLightbox(index)"
                         class="cursor-pointer transition-transform duration-300 hover:scale-105">
                        <img :src="getGalleryImageUrl(img.url, 'thumbnail')" 
                             :alt="img.title" 
                             loading="lazy"
                             decoding="async"
                             class="w-full aspect-square object-cover rounded-3xl">
                    </div>
                </div>
            </template>

            <!-- Lightbox -->
            <vue-easy-lightbox
                :visible="lightboxVisible"
                :imgs="lightboxImages"
                :index="lightboxIndex"
                @hide="closeLightbox"
                :moveDisabled="true"
                :dragDisabled="true"
                :swipeTolerance="50"
                :maskColor="'rgba(255, 182, 193, 0.9)'"
            >
                <!-- Custom toolbar -->
                <template v-slot:toolbar>
                    <div class="custom-toolbar">
                        <button @click="handlePrevImg" class="custom-lightbox-btn">
                            <i class="fa-solid fa-chevron-left text-2xl" style="color: #ffffff;"></i>
                        </button>
                        <button @click="closeLightbox" class="custom-lightbox-btn mx-4">
                            <i class="fa-solid fa-xmark text-2xl" style="color: #ffffff;"></i>
                        </button>
                        <button @click="handleNextImg" class="custom-lightbox-btn">
                            <i class="fa-solid fa-chevron-right text-2xl" style="color: #ffffff;"></i>
                        </button>
                    </div>
                </template>
            </vue-easy-lightbox>
        </div>
	</section>
	<!-- Portfolio section end -->
</template>

<script>
import VueEasyLightbox from 'vue-easy-lightbox'
import { contentfulClient } from '@/config/contentful'
import LoadingSkeleton from '@/components/LoadingSkeleton.vue'

export default {
    name: 'PortfolioSection',
    components: {
        VueEasyLightbox,
        LoadingSkeleton
    },
    data() {
        return {
            isLoading: true,
            lightboxVisible: false,
            lightboxIndex: 0,
            currentSlide: 0,
            gallery: {
                title: 'Gallery',
                galleryImages: []
            },
            processedImages: [],
            $window: {
                width: window.innerWidth
            },
            touchStartX: 0,
            touchEndX: 0,
            shouldUpdateSlide: true
        }
    },
    computed: {
        lightboxImages() {
            return this.processedImages.map(img => this.getGalleryImageUrl(img.url, 'lightbox'));
        }
    },
    watch: {
        lightboxIndex(newVal) {
            if (this.shouldUpdateSlide && this.lightboxVisible) {
                this.currentSlide = newVal;
            }
        }
    },
    methods: {
        getGalleryImageUrl(url, variant = 'thumbnail') {
            const normalizedUrl = url.startsWith('//') ? `https:${url}` : url
            const optimizedUrl = new URL(normalizedUrl)
            const imageVariants = {
                thumbnail: {
                    width: 480,
                    height: 480,
                    quality: 72,
                    fit: 'thumb'
                },
                mobile: {
                    width: 900,
                    height: 900,
                    quality: 74,
                    fit: 'thumb'
                },
                lightbox: {
                    width: 1800,
                    quality: 78
                }
            }
            const selectedVariant = imageVariants[variant] || imageVariants.thumbnail

            optimizedUrl.searchParams.set('fm', 'webp')
            optimizedUrl.searchParams.set('q', selectedVariant.quality)
            optimizedUrl.searchParams.set('w', selectedVariant.width)

            if (selectedVariant.height) {
                optimizedUrl.searchParams.set('h', selectedVariant.height)
                optimizedUrl.searchParams.set('fit', selectedVariant.fit)
            }

            return optimizedUrl.toString()
        },
        openLightbox(index) {
            this.lightboxIndex = index;
            this.lightboxVisible = true;
        },

        closeLightbox() {
            this.lightboxVisible = false;
        },

        handleNextImg() {
            this.shouldUpdateSlide = true;
            this.lightboxIndex = (this.lightboxIndex + 1) % this.processedImages.length;
        },

        handlePrevImg() {
            this.shouldUpdateSlide = true;
            this.lightboxIndex = this.lightboxIndex === 0 
                ? this.processedImages.length - 1 
                : this.lightboxIndex - 1;
        },

        // Mobile slide navigation
        handleNextClick() {
            this.shouldUpdateSlide = false;
            this.currentSlide = (this.currentSlide + 1) % this.processedImages.length;
        },
        
        handlePrevClick() {
            this.shouldUpdateSlide = false;
            this.currentSlide = this.currentSlide === 0 
                ? this.processedImages.length - 1 
                : this.currentSlide - 1;
        },

        touchStart(event) {
            this.touchStartX = event.touches[0].clientX;
        },

        touchMove(event) {
            this.touchEndX = event.touches[0].clientX;
        },

        touchEnd() {
            const swipeThreshold = 50;
            const swipeDistance = this.touchEndX - this.touchStartX;

            // Only handle swipe if we have both start and end coordinates
            if (this.touchStartX && this.touchEndX && Math.abs(swipeDistance) > swipeThreshold) {
                if (swipeDistance > 0) {
                    this.handlePrevClick();
                } else {
                    this.handleNextClick();
                }
            }

            // Reset touch coordinates
            this.touchStartX = 0;
            this.touchEndX = 0;
        },
        handleResize() {
            this.$window.width = window.innerWidth
        }
    },
    async created() {
        try {
            const response = await contentfulClient.getEntries({
                content_type: 'gallery',
                limit: 1,
                include: 2,
                select: ['fields.title', 'fields.galleryImages']
            })
            
            if (response.items && response.items.length > 0 && response.items[0].fields) {
                const galleryEntry = response.items[0].fields
                this.gallery.title = galleryEntry.title
                
                if (galleryEntry.galleryImages) {
                    this.processedImages = galleryEntry.galleryImages.map(image => ({
                        id: image.sys.id,
                        url: image.fields.file.url,
                        title: image.fields.title || 'Gallery Image'
                    }))
                }
            }
        } catch (error) {
            console.error('Error fetching gallery content:', error)
        } finally {
            this.isLoading = false
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize)
    },
    unmounted() {
        window.removeEventListener('resize', this.handleResize)
    }
}
</script>

<style scoped>
.custom-toolbar {
    position: fixed;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2001;
}

.custom-lightbox-btn {
    background-color: #B79CED;
    color: white;
    border-radius: 9999px;
    padding: 1rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 300ms;
    width: 3.5rem;
    height: 3.5rem;
}

.custom-lightbox-btn:hover {
    background-color: #9F8AE6;
    color: white;
    transform: scale(1.05);
}

/* Override default lightbox styles */
:deep(.vel-modal) {
    background-color: rgba(255, 182, 193, 0.9) !important;
}

:deep(.vel-img) {
    border-radius: 1rem;
}

:deep(.vel-loading) {
    color: #B79CED !important;
}

/* Hide default controls */
:deep(.vel-header),
:deep(.vel-toolbar) {
    display: none !important;
}

/* Hide the default close button */
:deep(.vel-btn-close) {
    display: none !important;
}

/* Hide any other default controls */
:deep(.vel-icon) {
    display: none !important;
}

.organic-shape {
    border-radius: 60% 40% 50% 50% / 40% 50% 50% 60%;
    transition: all 0.8s ease-in-out;
}

.organic-shape:hover {
    border-radius: 40% 60% 50% 50% / 50% 40% 60% 50%;
}
</style>
